import React from 'react'

export type ReviewFormType = 'hospital' | 'agency'

const BASE_TRUST_ITEMS = [
  {
    description: 'Your name and email are never shown publicly.',
    title: 'Completely anonymous',
  },
  {
    description: 'Quick ratings first, optional comments at the end.',
    title: 'Takes about 2 minutes',
  },
] as const

const CONTEXT_TRUST_ITEMS: Record<
  ReviewFormType,
  {
    description: string
    title: string
  }
> = {
  agency: {
    description: 'Your employment type and timing help others understand the context.',
    title: 'Work context',
  },
  hospital: {
    description: 'Your role and specialty help others understand the context.',
    title: 'Role & unit specific',
  },
}

function TrustCheckIcon() {
  return (
    <svg className="h-4 w-4 text-aqua-mint" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  )
}

type ReviewGetStartedIntroProps = {
  reviewType?: ReviewFormType
  showRoleGuidance?: boolean
}

export const ReviewGetStartedIntro: React.FC<ReviewGetStartedIntroProps> = ({
  reviewType = 'hospital',
  showRoleGuidance = false,
}) => {
  let description = 'Share your experience anonymously to help other bedside staff make better decisions.'

  if (showRoleGuidance) {
    description =
      reviewType === 'agency'
        ? 'Tell us how and when you worked with this agency.'
        : 'Select the role and unit you worked so your review helps the right bedside staff.'
  }

  return (
    <div className="mb-6 text-center">
      <h2 className="text-2xl font-bold text-graphite-grey md:text-3xl">Before you start</h2>
      <p className="mt-3 px-1 text-sm leading-relaxed text-gray-600 md:text-base">{description}</p>
    </div>
  )
}

type ReviewGetStartedAssurancesProps = {
  reviewType?: ReviewFormType
  showRoleAssurance?: boolean
}

export const ReviewGetStartedAssurances: React.FC<ReviewGetStartedAssurancesProps> = ({
  reviewType = 'hospital',
  showRoleAssurance = true,
}) => {
  const items = showRoleAssurance ? [...BASE_TRUST_ITEMS, CONTEXT_TRUST_ITEMS[reviewType]] : BASE_TRUST_ITEMS

  return (
    <div className="mt-6 space-y-3">
      {items.map((item) => (
        <div key={item.title} className="flex items-start gap-3 rounded-xl border border-sky-100 bg-sky-50 px-4 py-3.5">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-aqua-mint/15">
            <TrustCheckIcon />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-graphite-grey">{item.title}</p>
            <p className="mt-0.5 text-sm leading-snug text-gray-600">{item.description}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

export const ReviewGetStartedDisclaimer: React.FC = () => (
  <p className="mx-auto mt-4 max-w-2xl px-2 text-center text-xs leading-relaxed text-gray-500">
    Reviews are moderated for privacy, spam, and abuse — not to protect hospitals or agencies.
  </p>
)

function LockIcon() {
  return (
    <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  )
}

export const ReviewSubmitAnonymityReminder: React.FC = () => (
  <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-gray-500">
    <LockIcon />
    Your name and email are never shown publicly.
  </p>
)
